import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["button", "buttonContent", "body", "canvas", "failure", "progressPanel", "progressBar", "progressStage", "progressPercent", "progressTemplate"]
  static values = {
    url: String,
    statusUrl: String
  }

  connect() {
    this.pollingInterval = null
    if (this.hasButtonContentTarget) {
      this.originalContent = this.buttonContentTarget.innerHTML
    }
    this.checkInitialStatus()
  }

  disconnect() {
    this.stopPolling()
  }

  async generate(event) {
    event.preventDefault()
    this.showProcessing()

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          "Accept": "application/json"
        }
      })
      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || "Failed to generate artifact")
      }

      if (data.already) {
        this.showInfo(data.message || "Artifact generation is already in progress")
      }
      this.showProgressPanel()
      this.startPolling()
    } catch (error) {
      this.stopPolling()
      this.resetButton()
      this.showError(error.message)
    }
  }

  async checkInitialStatus() {
    try {
      const data = await this.fetchStatus()
      if (data.status === "processing") {
        this.showProcessing()
        this.showProgressPanel()
        this.updateProgress(data.progress, data.stage)
        this.startPolling()
      }
    } catch (_error) {
      // Artifact status is optional until one has been generated.
    }
  }

  startPolling() {
    this.stopPolling()
    this.pollingInterval = setInterval(() => this.checkStatus(), 2500)
  }

  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
      this.pollingInterval = null
    }
  }

  async checkStatus() {
    try {
      const data = await this.fetchStatus()

      if (data.status === "completed") {
        this.updateProgress(100, data.stage)
        this.stopPolling()
        this.resetButton()
        window.location.reload()
      } else if (data.status === "failed") {
        this.stopPolling()
        this.resetButton()
        if (this.hasProgressPanelTarget) this.progressPanelTarget.remove()
        this.showError(data.error || "Artifact generation failed")
      } else if (data.status === "idle") {
        this.stopPolling()
        this.resetButton()
      } else if (data.status === "processing") {
        this.showProgressPanel()
        this.updateProgress(data.progress, data.stage)
      }
    } catch (error) {
      console.error("Artifact status error:", error)
    }
  }

  async fetchStatus() {
    const response = await fetch(this.statusUrlValue, {
      headers: { "Accept": "application/json" }
    })
    if (!response.ok) throw new Error("Failed to check artifact status")
    return response.json()
  }

  showProcessing() {
    if (this.hasButtonTarget) this.buttonTarget.disabled = true
    if (this.hasButtonContentTarget) {
      this.buttonContentTarget.innerHTML = `
        <svg class="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M20 12a8 8 0 1 1-5.5-7.6" />
        </svg>
        <span>Generating artifact...</span>
      `
    }
  }

  // The pending state is server-rendered when an artifact record exists. When the
  // user starts generation from any other state, inject the same progress panel
  // from the inline <template> so the bar is visible immediately.
  showProgressPanel() {
    if (this.hasProgressPanelTarget) return
    if (!this.hasBodyTarget || !this.hasProgressTemplateTarget) return

    if (this.hasCanvasTarget) {
      if (this.hasFailureTarget) this.failureTarget.remove()
      this.bodyTarget.insertAdjacentHTML("afterbegin", this.progressTemplateTarget.innerHTML)
    } else {
      this.bodyTarget.innerHTML = this.progressTemplateTarget.innerHTML
    }
  }

  updateProgress(progress, stage) {
    const parsed = Number.parseInt(progress ?? 0, 10)
    const percent = Math.max(0, Math.min(100, Number.isNaN(parsed) ? 0 : parsed))

    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${percent}%`
      const track = this.progressBarTarget.closest('[role="progressbar"]')
      if (track) track.setAttribute("aria-valuenow", String(percent))
    }
    if (this.hasProgressPercentTarget) {
      this.progressPercentTarget.textContent = `${percent}%`
    }
    if (this.hasProgressStageTarget && stage) {
      this.progressStageTarget.textContent = stage
    }
  }

  resetButton() {
    if (this.hasButtonTarget) this.buttonTarget.disabled = false
    if (this.hasButtonContentTarget && this.originalContent) {
      this.buttonContentTarget.innerHTML = this.originalContent
    }
  }

  showError(message) {
    window.dispatchEvent(new CustomEvent("toast:show", {
      detail: { message, type: "error" }
    }))
  }

  showInfo(message) {
    window.dispatchEvent(new CustomEvent("toast:show", {
      detail: { message, type: "info" }
    }))
  }
}
